// src/pages/work-order-management-system/components/hooks/useBudgetItems.js
import { useEffect, useMemo, useState } from 'react';

// Helpers
const toNumber = (v) => {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
};

const withTotalsAndIds = (arr, prefix) => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item, idx) => {
        const cantidad = toNumber(item?.cantidad);
        const precioUnitario = toNumber(item?.precioUnitario);

        return {
            ...item,
            // si no trae id, se lo ponemos
            id: item?.id || `${prefix}-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
            cantidad,
            precioUnitario,
            total: cantidad * precioUnitario
        };
    });
};

/**
 * Maneja refacciones y servicios del presupuesto.
 * ✅ Ahora soporta precarga (editar) pasando prefill:
 * useBudgetItems(prefill)
 */
export const useBudgetItems = (prefill = null) => {
    const [refacciones, setRefacciones] = useState([]);
    const [servicios, setServicios] = useState([]);

    const [nuevaRefaccion, setNuevaRefaccion] = useState({
        nombre: '',
        cantidad: 1,
        precioUnitario: 0,
        codigo: '',
        origen: 'manual'
    });

    const [nuevoServicio, setNuevoServicio] = useState({
        descripcion: '',
        cantidad: 1,
        precioUnitario: 0
    });

    // ✅ Detecta de dónde vienen los items
    const prefillItems = useMemo(() => {
        const raw =
            prefill?.meta?.rawData || // por si algún día pasas el pdf completo
            prefill;

        const preRef =
            raw?.refacciones ||
            raw?.items?.refacciones ||
            raw?.data?.refacciones ||
            [];

        const preServ =
            raw?.servicios ||
            raw?.items?.servicios ||
            raw?.data?.servicios ||
            [];

        return {
            refacciones: Array.isArray(preRef) ? preRef : [],
            servicios: Array.isArray(preServ) ? preServ : []
        };
    }, [prefill]);

    // ✅ Prefill cuando abres "Editar"
    useEffect(() => {
        // Si no hay prefill, reset a vacío
        if (!prefill) {
            setRefacciones([]);
            setServicios([]);
            return;
        }

        // Si hay prefill pero no trae items, igual limpia
        const preRef = withTotalsAndIds(prefillItems.refacciones, 'ref');
        const preServ = withTotalsAndIds(prefillItems.servicios, 'serv');

        setRefacciones(preRef);
        setServicios(preServ);

        // Limpia inputs de "agregar"
        setNuevaRefaccion({
            nombre: '',
            cantidad: 1,
            precioUnitario: 0,
            codigo: '',
            origen: 'manual'
        });
        setNuevoServicio({
            descripcion: '',
            cantidad: 1,
            precioUnitario: 0
        });
    }, [prefill, prefillItems]);

    const displayNumber = (value) => (value === 0 || value === null ? '' : value);

    const handleAddRefaccion = () => {
        if (!nuevaRefaccion.nombre || nuevaRefaccion.cantidad <= 0) return;

        const nueva = {
            ...nuevaRefaccion,
            id: `ref-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            total: toNumber(nuevaRefaccion.cantidad) * toNumber(nuevaRefaccion.precioUnitario)
        };

        setRefacciones((prev) => [...prev, nueva]);
        setNuevaRefaccion({
            nombre: '',
            cantidad: 1,
            precioUnitario: 0,
            codigo: '',
            origen: 'manual'
        });
    };

    const handleAddServicio = () => {
        if (!nuevoServicio.descripcion || nuevoServicio.cantidad <= 0) return;

        const nuevo = {
            ...nuevoServicio,
            id: `serv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            total: toNumber(nuevoServicio.cantidad) * toNumber(nuevoServicio.precioUnitario)
        };

        setServicios((prev) => [...prev, nuevo]);
        setNuevoServicio({
            descripcion: '',
            cantidad: 1,
            precioUnitario: 0
        });
    };

    const handleRemoveRefaccion = (id) => {
        setRefacciones((prev) => prev.filter((ref) => ref.id !== id));
    };

    const handleRemoveServicio = (id) => {
        setServicios((prev) => prev.filter((serv) => serv.id !== id));
    };

    const handleUpdateRefaccion = (id, field, value) => {
        setRefacciones((prev) =>
            prev.map((ref) => {
                if (ref.id !== id) return ref;
                const updated = { ...ref, [field]: value };
                const cantidad = toNumber(field === 'cantidad' ? value : updated.cantidad);
                const precio = toNumber(field === 'precioUnitario' ? value : updated.precioUnitario);
                updated.cantidad = cantidad;
                updated.precioUnitario = precio;
                updated.total = cantidad * precio;
                return updated;
            })
        );
    };

    const handleUpdateServicio = (id, field, value) => {
        setServicios((prev) =>
            prev.map((serv) => {
                if (serv.id !== id) return serv;
                const updated = { ...serv, [field]: value };
                const cantidad = toNumber(field === 'cantidad' ? value : updated.cantidad);
                const precio = toNumber(field === 'precioUnitario' ? value : updated.precioUnitario);
                updated.cantidad = cantidad;
                updated.precioUnitario = precio;
                updated.total = cantidad * precio;
                return updated;
            })
        );
    };

    const tieneItems = refacciones.length > 0 || servicios.length > 0;

    return {
        refacciones,
        servicios,
        setRefacciones,
        setServicios,
        nuevaRefaccion,
        setNuevaRefaccion,
        nuevoServicio,
        setNuevoServicio,
        handleAddRefaccion,
        handleAddServicio,
        handleRemoveRefaccion,
        handleRemoveServicio,
        handleUpdateRefaccion,
        handleUpdateServicio,
        displayNumber,
        tieneItems
    };
}
